/**
 * C-Search → Probe. Ask the new load search (ADR-040) a question and see exactly what it answers.
 *
 * Two probes, both read-only:
 *   - SEARCH: one `SearchLoadsRequest`, paged with the service's own cursor — so paging is audited too.
 *   - LOOKUP: `GetLoad` by `SOURCE` + id or by shipment hash. It returns closed listings as well, which is how an
 *     operator answers "did the indexer see this load, and did it see it close?"
 */

import Box from '@mui/joy/Box'
import Button from '@mui/joy/Button'
import Card from '@mui/joy/Card'
import Chip from '@mui/joy/Chip'
import FormControl from '@mui/joy/FormControl'
import FormLabel from '@mui/joy/FormLabel'
import Input from '@mui/joy/Input'
import Sheet from '@mui/joy/Sheet'
import Table from '@mui/joy/Table'
import Typography from '@mui/joy/Typography'
import type { Listing, ListingStatus, LoadPipelineSearchLookupResponse, LoadPipelineSearchProbeResponse, SearchLoadsRequest } from '@numeo/types-core'
import { SEARCH_LIMITS } from '@numeo/types-core'
import { loadPipelineApi } from 'api/loadPipeline'
import * as React from 'react'
import { ErrorState, Stat, StatRow, formatNumber } from './components/shared'
import { SearchCriteriaForm, defaultSearchForm, toCriteria } from './components/SearchCriteriaForm'

export const SEARCH_UNREACHABLE = {
    title: 'Load search unreachable',
    body: 'main-service could not reach load-search-service, or LOAD_SEARCH_SERVICE_URL is not set for this environment. That says nothing about whether the indexer is writing.',
}

const STATUS_COLOR: Record<ListingStatus, 'success' | 'neutral' | 'danger' | 'warning'> = {
    AVAILABLE: 'success',
    COVERED: 'neutral',
    CANCELLED: 'danger',
    EXPIRED: 'warning',
}

const usd = (cents: number | undefined): string => (cents === undefined ? '—' : `$${(cents / 100).toLocaleString(undefined, { maximumFractionDigits: 0 })}`)
/** The rate search ranks by: a broker's quote when there is one, else the posted price (ADR-040.3). */
const searchRate = (l: Listing): number | undefined => l.price.rateCents ?? l.price.postedCurrentCents
const perMile = (l: Listing): string => {
    const cents = searchRate(l)
    return cents === undefined || !l.distanceMiles ? '—' : `$${(cents / 100 / l.distanceMiles).toFixed(2)}`
}
const rateTitle = (l: Listing): string => `posted ${usd(l.price.postedCurrentCents)} · broker's quote ${usd(l.price.offeredRateCents)}`
const place = (p: Listing['origin']): string => [p.city, p.state].filter(Boolean).join(', ') || `${p.lat.toFixed(2)}, ${p.lon.toFixed(2)}`

/** "How old" is the freshness question an operator is actually asking, so it reads as an age, not a timestamp. */
export function age(iso: string): string {
    const minutes = Math.round((Date.now() - Date.parse(iso)) / 60_000)
    if (minutes < 60) return `${minutes}m`
    if (minutes < 48 * 60) return `${Math.round(minutes / 60)}h`
    return `${Math.round(minutes / 1440)}d`
}

function ListingsTable({ listings, onSelect, selected }: { listings: readonly Listing[]; onSelect: (l: Listing) => void; selected: Listing | null }) {
    if (listings.length === 0) {
        return (
            <Typography level="body-sm" textColor="text.tertiary" sx={{ p: 2 }}>
                No listings.
            </Typography>
        )
    }
    return (
        <Table size="sm" hoverRow stickyHeader>
            <thead>
                <tr>
                    <th>Source</th>
                    <th>External id</th>
                    <th>Status</th>
                    <th>Stage</th>
                    <th>Origin</th>
                    <th>Destination</th>
                    <th>Pickup</th>
                    <th>Equip.</th>
                    <th style={{ textAlign: 'right' }}>Rate</th>
                    <th style={{ textAlign: 'right' }}>$/mi</th>
                    <th style={{ textAlign: 'right' }}>First seen</th>
                    <th style={{ textAlign: 'right' }}>Order at</th>
                    <th>Broker</th>
                </tr>
            </thead>
            <tbody>
                {listings.map((l) => (
                    <tr key={`${l.source}|${l.externalId}`} onClick={() => onSelect(l)} style={{ cursor: 'pointer' }} aria-selected={selected === l}>
                        <td>{l.source}</td>
                        <td>
                            <code>{l.externalId}</code>
                        </td>
                        <td>
                            <Chip size="sm" variant="soft" color={STATUS_COLOR[l.status]}>
                                {l.status}
                            </Chip>
                        </td>
                        <td>{l.stage ?? 'POSTED'}</td>
                        <td>{place(l.origin)}</td>
                        <td>{place(l.destination)}</td>
                        <td>{l.pickup.windowStart.slice(0, 10)}</td>
                        <td>{l.equipment}</td>
                        <td style={{ textAlign: 'right' }} title={rateTitle(l)}>
                            {usd(searchRate(l))}
                        </td>
                        <td style={{ textAlign: 'right' }}>{perMile(l)}</td>
                        <td style={{ textAlign: 'right' }} title={l.firstSeenAt}>
                            {age(l.firstSeenAt)}
                        </td>
                        <td style={{ textAlign: 'right' }} title={l.orderAt}>
                            {age(l.orderAt)}
                        </td>
                        <td>{l.broker.name ?? l.broker.mc ?? '—'}</td>
                    </tr>
                ))}
            </tbody>
        </Table>
    )
}

function ListingDetail({ listing }: { listing: Listing | null }) {
    if (!listing) return null
    return (
        <Card variant="soft" sx={{ mt: 2 }}>
            <Typography level="title-sm">
                {listing.source}|{listing.externalId} — shipment <code>{listing.loadHash}</code>
            </Typography>
            <Box component="pre" sx={{ m: 0, fontSize: 12, maxHeight: 360, overflow: 'auto' }}>
                {JSON.stringify(listing, null, 2)}
            </Box>
        </Card>
    )
}

function LookupCard({ projectName }: { projectName: string }) {
    const [source, setSource] = React.useState('DAT_EXTENSION')
    const [externalId, setExternalId] = React.useState('')
    const [loadHash, setLoadHash] = React.useState('')
    const [result, setResult] = React.useState<LoadPipelineSearchLookupResponse | null>(null)
    const [error, setError] = React.useState<unknown>(null)
    const [loading, setLoading] = React.useState(false)
    const [selected, setSelected] = React.useState<Listing | null>(null)

    const run = async () => {
        setLoading(true)
        setError(null)
        setSelected(null)
        try {
            const caller = { projectName }
            setResult(
                await loadPipelineApi.searchLookup(loadHash.trim() ? { loadHash: loadHash.trim(), caller } : { source, externalId: externalId.trim(), caller })
            )
        } catch (err) {
            setError(err)
        } finally {
            setLoading(false)
        }
    }

    return (
        <Sheet variant="outlined" sx={{ borderRadius: 'sm', p: 2, mt: 3 }}>
            <Typography level="title-md">Look up one load</Typography>
            <Typography level="body-xs" textColor="text.tertiary" sx={{ mb: 1 }}>
                By source and id, or every listing of a shipment by hash. Closed listings are returned too, so a missing row means the indexer never wrote it.
            </Typography>
            <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-end', flexWrap: 'wrap', mb: 1 }}>
                <FormControl size="sm">
                    <FormLabel>Source</FormLabel>
                    <Input size="sm" value={source} onChange={(e) => setSource(e.target.value.toUpperCase())} sx={{ width: 160 }} />
                </FormControl>
                <FormControl size="sm">
                    <FormLabel>External id</FormLabel>
                    <Input size="sm" value={externalId} onChange={(e) => setExternalId(e.target.value)} sx={{ width: 220 }} />
                </FormControl>
                <Typography level="body-sm" sx={{ pb: 0.75 }}>
                    or
                </Typography>
                <FormControl size="sm">
                    <FormLabel>Load hash</FormLabel>
                    <Input size="sm" value={loadHash} onChange={(e) => setLoadHash(e.target.value)} sx={{ width: 300 }} />
                </FormControl>
                <Button size="sm" variant="outlined" onClick={run} loading={loading} disabled={!loadHash.trim() && !(source && externalId.trim())}>
                    Look up
                </Button>
            </Box>
            {error ? <ErrorState error={error} unreachable={SEARCH_UNREACHABLE} /> : null}
            {result ? (
                <>
                    <Typography level="body-xs" textColor="text.tertiary" sx={{ mb: 1 }}>
                        {formatNumber(result.listings.length)} listing(s) in {result.latencyMs} ms
                    </Typography>
                    <ListingsTable listings={result.listings} onSelect={setSelected} selected={selected} />
                    <ListingDetail listing={selected} />
                </>
            ) : null}
        </Sheet>
    )
}

export default function LoadPipelineSearchProbePage() {
    const [form, setForm] = React.useState(defaultSearchForm)
    const [pageSize, setPageSize] = React.useState(50)
    const [pages, setPages] = React.useState<LoadPipelineSearchProbeResponse[]>([])
    const [error, setError] = React.useState<unknown>(null)
    const [loading, setLoading] = React.useState(false)
    const [selected, setSelected] = React.useState<Listing | null>(null)

    const run = async (cursor?: string) => {
        const { criteria } = toCriteria(form)
        if (!criteria) return
        const request: SearchLoadsRequest = { criteria, caller: { projectName: form.projectName }, sort: form.sort, pageSize, ...(cursor ? { cursor } : {}) }
        setLoading(true)
        setError(null)
        try {
            const page = await loadPipelineApi.searchProbe(request)
            setPages((prev) => (cursor ? [...prev, page] : [page]))
            if (!cursor) setSelected(null)
        } catch (err) {
            setError(err)
        } finally {
            setLoading(false)
        }
    }

    const listings = pages.flatMap((p) => p.listings)
    const last = pages[pages.length - 1]
    const latencies = pages.map((p) => p.latencyMs)
    const statuses = listings.reduce<Record<string, number>>((acc, l) => ({ ...acc, [l.status]: (acc[l.status] ?? 0) + 1 }), {})
    const shipments = new Set(listings.map((l) => l.loadHash)).size
    const newestSeen = listings.reduce<string | null>((max, l) => (max === null || l.firstSeenAt > max ? l.firstSeenAt : max), null)

    return (
        <Box>
            <SearchCriteriaForm
                value={form}
                onChange={setForm}
                onSubmit={() => run()}
                submitting={loading && pages.length === 0}
                submitLabel="Search"
                showSort
            />
            <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', mb: 2 }}>
                <Typography level="body-sm">Page size</Typography>
                <Input
                    size="sm"
                    type="number"
                    value={pageSize}
                    onChange={(e) => setPageSize(Math.min(SEARCH_LIMITS.pageSize, Math.max(1, Number(e.target.value) || 1)))}
                    sx={{ width: 90 }}
                />
                <Typography level="body-xs" textColor="text.tertiary">
                    max {SEARCH_LIMITS.pageSize}
                </Typography>
            </Box>

            {error ? <ErrorState error={error} unreachable={SEARCH_UNREACHABLE} /> : null}

            {last ? (
                <>
                    <StatRow>
                        <Stat
                            label="Listings"
                            value={formatNumber(listings.length)}
                            hint={`${pages.length} page(s)${last.nextCursor ? ', more available' : ', last page'}`}
                        />
                        <Stat label="Shipments" value={formatNumber(shipments)} hint="distinct loadHash — listings sharing one are one load" />
                        <Stat
                            label="Latency"
                            value={`${last.latencyMs} ms`}
                            hint={pages.length > 1 ? `pages: ${latencies.join(' / ')} ms` : 'main-service → search, round trip'}
                            tone={last.latencyMs > 500 ? 'warning' : 'neutral'}
                        />
                        <Stat
                            label="Not available"
                            value={formatNumber(listings.length - (statuses.AVAILABLE ?? 0))}
                            hint={Object.entries(statuses)
                                .map(([s, n]) => `${s} ${n}`)
                                .join(' · ')}
                            tone={listings.length - (statuses.AVAILABLE ?? 0) > 0 ? 'warning' : 'neutral'}
                        />
                        <Stat label="Newest first seen" value={newestSeen ? `${age(newestSeen)} ago` : '—'} hint="how fresh the freshest result is" />
                    </StatRow>

                    <Sheet variant="outlined" sx={{ borderRadius: 'sm', overflow: 'auto', maxHeight: 560 }}>
                        <ListingsTable listings={listings} onSelect={setSelected} selected={selected} />
                    </Sheet>
                    {last.nextCursor ? (
                        <Button size="sm" variant="outlined" sx={{ mt: 1 }} loading={loading} onClick={() => run(last.nextCursor ?? undefined)}>
                            Next page
                        </Button>
                    ) : null}
                    <ListingDetail listing={selected} />
                </>
            ) : null}

            <LookupCard projectName={form.projectName} />
        </Box>
    )
}
